import React, { useEffect, useMemo, useState } from 'react'
import Lottie from 'react-lottie'
import { withRouter } from 'react-router-dom'

import { AcornsIcon } from '@acorns/icons'
import { ThiccMenu } from '@acorns/web-components'
import { WithOptimizelyProps } from '@optimizely/react-sdk'
import {
  branch,
  compose,
  fromRenderProps,
  renderComponent,
  withHandlers,
  withProps,
} from 'recompose'

import FundingAnimation from 'assets/animations/fundingAnimation'
import CenteredSpinner from 'components/CenteredSpinner'
import withHeroText from 'components/HOCs/withHeroText'
import withLiveContent, { LiveContentProps } from 'components/HOCs/WithLiveContent'
import withPartnerProps from 'components/HOCs/WithPartnerProps'
import Nav from 'components/Nav'
import getDefaultEventData from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, PageName } from 'generated/types'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import { withSkipBankLinking } from 'utils/mutators/bank-linking/bankLinking'
import { withOptimizely } from 'utils/Optimizely'

import {
  AnimationContainer,
  Container,
  Footnote,
  LaterImage,
  LayoutNoMobilePadding,
  Text,
  TextLater,
  Title,
  TitleLater,
} from './styled-components'

type Props = {
  handleClick: () => void
  handleManualFundingClick: () => void
  header: string
  title: string
  text: string
  submitCta: string
  footnote: string
} & WithOptimizelyProps &
  WithFlowRouterProps &
  LiveContentProps &
  FunnelContextProps

const fundingOptionsLabel = {
  automatic: 'Automatic',
  manual: 'Manual',
}

export const enhance = compose<Props, {}>(
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ isBackEnabled, isDynamicFunnelEnabled, routeToPage }) => ({
      isBackEnabled,
      isDynamicFunnelEnabled,
      routeToPage,
    }),
  ),
  withRouter,
  withFlowRouter,
  withOptimizely,
  withPartnerProps,
  withLiveContent,
  withSkipBankLinking,
  withHeroText,
  withProps(({ liveContent, heroText }) => {
    return {
      header: liveContent('hero_header', {
        remoteVariableKey: 'bank_link_interstitial_header',
      }),
      title: heroText.title,
      text: liveContent('hero_text'),
      submitCta: liveContent('submit_cta', { fallback: 'Next' }),
      footnote: heroText.footnote,
    }
  }),
  withHandlers({
    handleClick: ({ trackOptimizely, route, isDynamicFunnelEnabled, routeToPage }) => () => {
      trackOptimizely('link_your_bank.cta_clicked')
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.FUNDING.AUTOMATIC_BANK_LINK_BUTTON),
          ...getDefaultEventData('Button Tapped'),
          destination: 'registrationLinkBankSearch',
          cta_title: fundingOptionsLabel.automatic,
        },
      })
      isDynamicFunnelEnabled
        ? routeToPage(PageName.Funding, ActionType.Continue)
        : route({ from: FlowRoute.Funding, action: 'continue' })
    },
    handleManualFundingClick: ({
      trackOptimizely,
      route,
      flow,
      isDynamicFunnelEnabled,
      routeToPage,
    }) => () => {
      trackOptimizely('link_your_bank.cta_clicked')
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.FUNDING.MANUAL_BANK_LINK_BUTTON),
          ...getDefaultEventData('Button Tapped'),
          destination:
            flow !== 'later' ? 'registrationLinkBankManual' : 'laterRegistrationLinkBankManual',
          cta_title: fundingOptionsLabel.manual,
        },
      })
      isDynamicFunnelEnabled
        ? routeToPage(PageName.Funding, ActionType.Manual)
        : route({ from: FlowRoute.Funding, action: 'manual' })
    },
  }),
  branch(({ loading }) => loading, renderComponent(CenteredSpinner)),
)

export const Funding: React.FC<Props> = ({
  handleClick,
  handleManualFundingClick,
  header,
  title,
  text,
  flow,
  footnote,
  isDynamicFunnelEnabled,
  isBackEnabled,
}) => {
  const [isSubmitBtnDisabled, setIsSubmitBtnDisabled] = useState(true)
  const [showAnimation, setShowAnimation] = useState(false)
  const bankMenuItems = useMemo(
    () => [
      {
        icon: (
          <AcornsIcon icon={AcornsIcon.Icon.ProductLaterGreen} height={'24px'} width={'24px'} />
        ),
        label: fundingOptionsLabel.automatic,
        labelDescription: '(Most popular) Connect to bank with Plaid',
        action: { callback: handleClick },
        withChevron: true,
        trackId: TRACKER_IDS.FUNDING.LINK_YOUR_BANK_BUTTON,
        isDisabled: isSubmitBtnDisabled,
        testId: 'button',
      },
      {
        icon: (
          <AcornsIcon icon={AcornsIcon.Icon.ProductSpendGreen} height={'24px'} width={'24px'} />
        ),
        label: fundingOptionsLabel.manual,
        labelDescription: 'Enter account & routing information',
        action: { callback: handleManualFundingClick },
        withChevron: true,
      },
    ],
    [isSubmitBtnDisabled, handleClick],
  )

  const delayedRenderer = (callback, time) => {
    setTimeout(() => {
      callback()
    }, time)
  }

  useEffect(() => {
    delayedRenderer(() => setIsSubmitBtnDisabled(false), 2000)
    delayedRenderer(() => setShowAnimation(true), 1000)
  }, [])
  const fundingAnimationOptions = {
    loop: false,
    autoplay: showAnimation,
    animationData: FundingAnimation,
  }
  return (
    <>
      <Nav back={isDynamicFunnelEnabled && isBackEnabled(PageName.Funding)} />
      <LayoutNoMobilePadding footnote={footnote}>
        <Container>
          {flow === 'later' ? (
            <>
              <LaterImage />
              <TitleLater>{header}</TitleLater>
              <TextLater>{text}</TextLater>
            </>
          ) : (
            <>
              <AnimationContainer>
                <Lottie isClickToPauseDisabled={true} options={fundingAnimationOptions} />
              </AnimationContainer>
              <Title>{title}</Title>
              <Text>{text}</Text>
            </>
          )}

          <ThiccMenu className={'ThiccContainer'} menuItems={bankMenuItems} hasBorder={true} />
        </Container>
        <Footnote>
          <i>{footnote}</i>
        </Footnote>
      </LayoutNoMobilePadding>
    </>
  )
}

export default enhance(Funding)
